/**
 * Inbox — leads list (filters + cursor paging) and lead detail: contact,
 * diagnosis, photos, consent record, the merged timeline, delivery attempts
 * with resend, reassign to a contractor, close with an outcome, and reopen.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { LEAD_OUTCOMES, LEAD_OUTCOME_CLASSES, LEAD_STATUSES, type Destination, type LeadSummary, type TimelineEntry } from '../api/types';
import { TRADES, tradeLabel } from '../config/model';
import { unmatchedCopy } from '../leads/unmatched';
import { networkFallbackSentence } from '../leads/networkFallback';
import { AcceptLeadSlot } from '../leads/AcceptLeadSlot';
import { QuoteSlot } from '../leads/QuoteSlot';
import { Link, useRouter } from '../router';
import { useDestinationLookup } from '../state/destination-labels';
import { deliverySentence, formatPhone } from '../leads/format';
import { Empty, ErrorNotice, Field, Loading, Modal, NotSet, PageHead, Time, formatDateTime, humanize, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

const STATUS_TONE: Record<string, string> = {
  new: 'accent',
  routing: 'info',
  delivered: 'info',
  accepted: 'ok',
  connected: 'ok',
  declined: 'bad',
  unmatched: 'warn',
  revoked: 'bad',
  closed: '',
};

const URGENCY_TONE: Record<string, string> = { emergency: 'bad', urgent: 'warn', routine: '' };

/** The Test badge every test lead carries (they never reach contractors). */
export function TestBadge({ style }: { style?: CSSProperties }) {
  return (
    <span className="tag test test-badge" style={style}>
      Test
    </span>
  );
}

const hostOnly = (origin: string) => origin.replace(/^https?:\/\//, '');

/**
 * Is the widget live, and where? Asked only when the unfiltered list is empty.
 * The most recent website a LIVE key was seen on (install health, owners,
 * admins and developers), else any live impression in the last 90 days
 * (the funnel, which an operator can read too). `null` while unknown.
 */
function useLiveState(enabled: boolean): { live: boolean; origin: string | null } | null {
  const { api, role } = useSession();
  const canHealth = can(role, 'installHealth');
  const canFunnel = can(role, 'funnel');
  const health = useQuery(() => api.installHealth(), [], { enabled: enabled && canHealth });
  const funnel = useQuery(
    () => api.funnel({ from: new Date(Date.now() - 90 * 86_400_000).toISOString(), to: new Date().toISOString() }),
    [],
    { enabled: enabled && canFunnel },
  );
  if (!enabled) return null;
  const seen = (health.data?.origins ?? [])
    .filter((o) => o.last_impression_at && (o.site_key ?? '').startsWith('fx_live_'))
    .sort((a, b) => String(b.last_impression_at).localeCompare(String(a.last_impression_at)));
  if (seen[0]) return { live: true, origin: seen[0].origin };
  if ((funnel.data?.impressions ?? 0) > 0) return { live: true, origin: null };
  const pending = (canHealth && health.loading) || (canFunnel && funnel.loading);
  if (pending) return null;
  return { live: false, origin: null };
}

/** The list's empty state: filters that match nothing, or no leads at all (live or not yet). */
export function LeadsEmpty({ filtered, onClear, liveOnly = false }: { filtered: boolean; onClear: () => void; liveOnly?: boolean }) {
  const { role } = useSession();
  const state = useLiveState(!filtered);
  if (filtered && liveOnly) {
    return (
      <Empty level={2} title="No live leads yet">
        <div className="stack" style={{ alignItems: 'center' }}>
          <p style={{ margin: 0 }}>Test leads are hidden by the “Live only” filter.</p>
          <button type="button" className="btn" onClick={onClear}>
            Show all leads
          </button>
        </div>
      </Empty>
    );
  }
  if (filtered) {
    return (
      <Empty level={2} title="No leads match these filters">
        <button type="button" className="btn" onClick={onClear}>
          Clear filters
        </button>
      </Empty>
    );
  }
  const testLink = (
    <Link className="btn btn-primary" to="/setup/test">
      Test a lead
    </Link>
  );
  if (!state) return <Empty level={2} title="No leads yet" />;
  if (state.live) {
    return (
      <Empty level={2} title="No leads yet">
        <div className="stack" style={{ alignItems: 'center' }}>
          <p style={{ margin: 0 }}>
            Your widget is live{state.origin ? <> on <strong className="wrap-anywhere">{hostOnly(state.origin)}</strong></> : null} — send a test lead to see one
            here.
          </p>
          {testLink}
        </div>
      </Empty>
    );
  }
  return (
    <Empty level={2} title="No leads yet">
      <div className="stack" style={{ alignItems: 'center' }}>
        <p style={{ margin: 0 }}>
          Your widget hasn’t loaded on a live website yet. Once it does, requests appear here within seconds. You can send a test lead now.
        </p>
        <div className="row" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
          {testLink}
          {can(role, 'sites') ? (
            <Link className="btn" to="/setup/install">
              Install the widget
            </Link>
          ) : null}
        </div>
      </div>
    </Empty>
  );
}

function contactLine(l: LeadSummary): string {
  const c = l.contact;
  if (!c) return l.contact_purged_at ? 'Contact purged' : '—';
  return [c.first_name, [c.city, c.state].filter(Boolean).join(', '), c.postal_code].filter(Boolean).map(String).join(' · ') || '—';
}

/** The list's filters, kept in the URL so Back/Forward and a shared link keep them. */
export const LEAD_FILTER_KEYS = ['status', 'trade', 'urgency', 'mode'] as const;

export function Inbox() {
  const { api } = useSession();
  const { search, navigate } = useRouter();
  const filters = useMemo(() => {
    const out: Record<string, string | undefined> = {};
    for (const k of LEAD_FILTER_KEYS) out[k] = search.get(k) || undefined;
    return out;
  }, [search]);
  const setFilters = (next: Record<string, string | undefined>) => {
    const q = new URLSearchParams();
    for (const k of LEAD_FILTER_KEYS) if (next[k]) q.set(k, next[k] as string);
    const qs = q.toString();
    navigate(`/leads${qs ? `?${qs}` : ''}`, { keepScroll: true });
  };
  const [pages, setPages] = useState<LeadSummary[][]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const first = useQuery(() => api.listLeads(filters), [filters], { pollMs: 30_000 });
  const more = useMutation(async () => {
    if (!cursor) return null;
    const res = await api.listLeads(filters, cursor);
    setPages((p) => [...p, res.items]);
    setCursor(res.next_cursor);
    return res;
  });
  useEffect(() => {
    setPages([]);
    setCursor(first.data?.next_cursor ?? null);
  }, [first.data]);
  const items = [...(first.data?.items ?? []), ...pages.flat()];
  const set = (k: string, v: string) => setFilters({ ...filters, [k]: v || undefined });
  const filtered = Object.values(filters).some(Boolean);
  const liveOnly = filters.mode === 'live' && LEAD_FILTER_KEYS.every((k) => k === 'mode' || !filters[k]);

  return (
    <div>
      <PageHead title="Leads" lede="Every request your widget has sent. Test leads carry a Test badge and never reach contractors." />
      <section className="panel">
        <div className="panel-head" style={{ justifyContent: 'flex-start' }}>
          <label className="row small" style={{ gap: 6 }} htmlFor="inbox-filter-status">
            <span className="label">Status</span>
            <select id="inbox-filter-status" className="input" style={{ minHeight: 30, width: 'auto' }} value={filters.status ?? ''} onChange={(e) => set('status', e.target.value)}>
              <option value="">All</option>
              {LEAD_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="row small" style={{ gap: 6 }} htmlFor="inbox-filter-trade">
            <span className="label">Trade</span>
            <select id="inbox-filter-trade" className="input" style={{ minHeight: 30, width: 'auto' }} value={filters.trade ?? ''} onChange={(e) => set('trade', e.target.value)}>
              <option value="">All</option>
              {TRADES.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label className="row small" style={{ gap: 6 }} htmlFor="inbox-filter-urgency">
            <span className="label">Urgency</span>
            <select id="inbox-filter-urgency" className="input" style={{ minHeight: 30, width: 'auto' }} value={filters.urgency ?? ''} onChange={(e) => set('urgency', e.target.value)}>
              <option value="">All</option>
              <option value="emergency">Emergency</option>
              <option value="urgent">Urgent</option>
              <option value="routine">Routine</option>
            </select>
          </label>
          <label className="row small" style={{ gap: 6 }} htmlFor="inbox-filter-mode">
            <span className="label">Mode</span>
            <select id="inbox-filter-mode" className="input" style={{ minHeight: 30, width: 'auto' }} value={filters.mode ?? ''} onChange={(e) => set('mode', e.target.value)}>
              <option value="">Live and test</option>
              <option value="live">Live only</option>
              <option value="test">Test only</option>
            </select>
          </label>
        </div>
        {first.loading && !first.data ? (
          <Loading />
        ) : first.error ? (
          <div className="panel-body">
            <ErrorNotice error={first.error} />
          </div>
        ) : items.length ? (
          <>
            <div className="table-wrap collapse">
              <table className="data">
                <caption className="visually-hidden">Leads</caption>
                <thead>
                  <tr>
                    <th scope="col">Problem</th>
                    <th scope="col">Urgency</th>
                    <th scope="col">Status</th>
                    <th scope="col">Homeowner</th>
                    <th scope="col">From</th>
                    <th scope="col" className="num">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <Link className="rowlink" to={`/leads/${l.id}`}>
                          {l.problem_label ?? humanize(l.problem_code ?? l.trade)}
                        </Link>{' '}
                        {l.test ? <TestBadge /> : null}
                        <div className="small faint">{l.trade_label ?? tradeLabel(l.trade)}</div>
                      </td>
                      <td>
                        <span className={`tag ${URGENCY_TONE[l.urgency] ?? ''}`}>{humanize(l.urgency)}</span>
                      </td>
                      <td>
                        <span className={`tag ${STATUS_TONE[l.status] ?? ''}`}>{humanize(l.status)}</span>
                      </td>
                      <td className="small">{contactLine(l)}</td>
                      <td className="small mono faint">{l.origin.replace(/^https?:\/\//, '')}</td>
                      <td className="num small">
                        <Time iso={l.created_at} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="cards">
              {items.map((l) => (
                <Link key={l.id} to={`/leads/${l.id}`}>
                  <span className="row-between">
                    <strong style={{ color: 'var(--ink)' }}>{l.problem_label ?? l.trade_label ?? tradeLabel(l.trade)}</strong>
                    <span className="small muted">
                      <Time iso={l.created_at} />
                    </span>
                  </span>
                  <span className="row" style={{ gap: 6 }}>
                    <span className={`tag ${STATUS_TONE[l.status] ?? ''}`}>{humanize(l.status)}</span>
                    <span className={`tag ${URGENCY_TONE[l.urgency] ?? ''}`}>{humanize(l.urgency)}</span>
                    {l.test ? <TestBadge /> : null}
                  </span>
                  <span className="small muted">{contactLine(l)}</span>
                </Link>
              ))}
            </div>
            {cursor ? (
              <div className="panel-body" style={{ textAlign: 'center' }}>
                <button type="button" className="btn" disabled={more.pending} onClick={() => void more.run()}>
                  {more.pending ? 'Loading…' : 'Load more'}
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <LeadsEmpty filtered={filtered} liveOnly={liveOnly} onClear={() => setFilters({})} />
        )}
      </section>
    </div>
  );
}

function TimelineItem({ t, onResend, resending, destination, vendorName }: { t: TimelineEntry; onResend: (id: number) => void; resending: boolean; destination: Destination | null; vendorName: (id: unknown) => string }) {
  const d = t.detail;
  const who = vendorName(d.vendor_id);
  let text = humanize(t.type);
  if (t.type === 'created') text = `Received from ${String(d.origin ?? '').replace(/^https?:\/\//, '')}`;
  if (t.type === 'assigned') text = `Offered to ${who} (rank ${String(d.rank)})`;
  // The unmatched notice rides on a `lead.created` delivery; `detail.notice` says which it is (#900).
  // Said as what happened ("Emailed to ops@…"), not as the raw event (third live review, 2026-09-27).
  if (t.type === 'delivered') {
    text = deliverySentence(d, destination);
    // The unmatched notice says why nobody was matched (migration 077).
    const why = d.notice === 'unmatched' ? unmatchedCopy(d.reason) : null;
    if (why) text = `${text} · ${why.short}`;
  }
  // Server migration 094: handed to the matching network, with the result.
  if (t.type === 'network_fallback') text = networkFallbackSentence({ status: d.status as never, reason: d.reason, sent_at: d.sent_at });
  // A photo the homeowner added from the widget after sending the request.
  if (t.type === 'media_added') text = 'Homeowner added a photo';
  if (t.type === 'reopened') text = `Reopened${d.from_outcome_class ? ` (was ${humanize(String(d.from_outcome_class))})` : ''}`;
  if (t.type === 'accepted' || t.type === 'declined' || t.type === 'contacted') text = `${who} ${t.type}${d.reason ? `: ${String(d.reason)}` : ''}`;
  // The server maps a lapsed offer onto `declined` (§Leads' closed type list)
  // and keeps the raw `timeout` in `detail.response` so it stays legible: the
  // contractor did nothing, which is not the same as saying no.
  if (t.type === 'declined' && d.response === 'timeout') text = `${who} did not respond before the deadline`;
  if (t.type === 'closed') text = `Closed · ${humanize(String(d.outcome ?? ''))}${d.outcome_class ? ` (${humanize(String(d.outcome_class))})` : ''}`;
  const failed = t.type === 'delivered' && (d.status === 'failed' || d.status === 'gave_up');
  return (
    <li className={`t-${t.type}`}>
      <span className="n" aria-hidden="true" />
      <div className="row-between">
        <span className="small">
          <strong style={{ color: 'var(--ink)' }}>{text}</strong>
        </span>
        <span className="small faint num">{formatDateTime(t.ts)}</span>
      </div>
      {t.type === 'delivered' ? (
        <button
          type="button"
          className={`btn btn-sm resend-btn ${failed ? 'btn-primary' : ''}`}
          style={{ marginTop: 4 }}
          disabled={resending}
          aria-label={`Resend: ${text}`}
          onClick={() => onResend(Number(d.delivery_id))}
        >
          Resend
        </button>
      ) : null}
    </li>
  );
}

/** Outcomes a person closes with; `reopened` is what a reopen writes, never a choice. */
const CLOSE_OUTCOMES = LEAD_OUTCOMES.filter((o) => o !== 'reopened');

export function LeadDetail({ id }: { id: string }) {
  const { api, role } = useSession();
  const toast = useToast();
  const lead = useQuery(() => api.getLead(id), [id]);
  // Polls while the lead can still change. A closed lead's timeline is
  // final until someone reopens it (which reloads it here), and useQuery
  // skips every tick while the tab is hidden.
  const closedLead = lead.data?.status === 'closed';
  const timeline = useQuery(() => api.leadTimeline(id), [id], { pollMs: closedLead ? undefined : 10_000 });
  const destinationOf = useDestinationLookup();
  // Names for the timeline's offers. The whole roster (`active: 'all'`): a
  // contractor disabled since still has history on older leads. A failed or
  // pending read falls back to the id rather than hiding the entry.
  const names = useQuery(() => api.listVendors({ active: 'all', limit: 200 }), []);
  const vendorName = (vendorId: unknown): string => {
    const found = names.data?.items.find((v) => v.id === vendorId);
    return found?.name ?? `contractor ${String(vendorId)}`;
  };
  const resend = useMutation((deliveryId: number) => api.resendDelivery(id, deliveryId));
  const [closing, setClosing] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  // No default: "Dispatched" preselected made every hurried close claim a
  // dispatch that never happened, and the funnel counted it.
  const [outcome, setOutcome] = useState<string>('');
  const [outcomeClass, setOutcomeClass] = useState<string>('');
  const [note, setNote] = useState('');
  const [vendor, setVendor] = useState('');
  const [reason, setReason] = useState('');
  const close = useMutation(() => api.closeLead(id, { outcome, ...(outcomeClass ? { outcome_class: outcomeClass } : {}), ...(note.trim() ? { note: note.trim() } : {}) }));
  const reopen = useMutation(() => api.reopenLead(id));
  const reassign = useMutation(() => api.reassignLead(id, vendor.trim(), reason.trim() || undefined));
  // Read up front, so a Reassign with nobody to reassign to is disabled and says why.
  const roster = useQuery(() => api.listVendors({ active: 'true', limit: 200 }), []);
  const reachable = (roster.data?.items ?? []).filter((v) => !v.do_not_contact);

  if (lead.loading) return <Loading />;
  if (lead.error || !lead.data) return <ErrorNotice error={lead.error} title="Could not load this lead" />;
  const l = lead.data;
  const c = (l.contact ?? {}) as Record<string, unknown>;
  // Contractor pages: an accept, a quote or a project change reloads both.
  const refetch = () => {
    void lead.reload();
    void timeline.reload();
  };
  const dx = l.diagnosis;
  const cost = dx.cost_estimate;
  // Why Reassign is off, said next to it (a disabled button alone explained nothing).
  const reassignBlocked = l.test
    ? 'Test leads never go to contractors'
    : roster.data && reachable.length === 0
      ? 'No contractors yet'
      : null;
  const phone = typeof c.phone_1 === 'string' ? c.phone_1 : null;
  const unmatched = unmatchedCopy(l.unmatched_reason);

  return (
    <div>
      <PageHead
        eyebrow={<Link to="/leads">Leads</Link>}
        title={
          <>
            {dx.problem_label ?? humanize(dx.problem_code ?? dx.trade ?? 'Lead')} {l.test ? <TestBadge style={{ verticalAlign: 'middle' }} /> : null}
          </>
        }
        lede={
          <>
            <span className={`tag ${STATUS_TONE[l.status] ?? ''}`}>{humanize(l.status)}</span>{' '}
            <span className={`tag ${URGENCY_TONE[l.urgency] ?? ''}`}>{humanize(l.urgency)}</span> · received <Time iso={l.created_at} /> ·{' '}
            <span className="mono small">{l.lead_id}</span>
          </>
        }
        actions={
          <>
            <span className="reassign-wrap">
              <button
                type="button"
                className="btn"
                onClick={() => setReassigning(true)}
                disabled={Boolean(reassignBlocked)}
                aria-describedby={reassignBlocked ? 'reassign-why' : undefined}
                title={reassignBlocked ?? undefined}
              >
                Reassign
              </button>
              {reassignBlocked ? (
                <span id="reassign-why" className="small muted reassign-why">
                  {reassignBlocked}
                </span>
              ) : null}
            </span>
            {l.status === 'closed' && can(role, 'reopenLead') ? (
              <button
                type="button"
                className="btn"
                disabled={reopen.pending}
                onClick={async () => {
                  if (await reopen.run()) {
                    toast('Lead reopened');
                    void lead.reload();
                    void timeline.reload();
                  }
                }}
              >
                {reopen.pending ? 'Reopening…' : 'Reopen'}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setOutcome('');
                setOutcomeClass('');
                close.reset();
                setClosing(true);
              }}
              disabled={l.status === 'closed'}
            >
              {l.status === 'closed' ? 'Closed' : 'Close lead'}
            </button>
          </>
        }
      />
      <ErrorNotice error={reopen.error} />
      {unmatched ? (
        <div className="notice warn" role="status" style={{ marginBottom: 16 }}>
          <strong>No contractor got this lead: {unmatched.short}.</strong> {unmatched.why} {unmatched.fix}{' '}
          <Link to={unmatched.to}>Fix it</Link>
          {l.network_fallback ? <div className="small" style={{ marginTop: 4 }}>{networkFallbackSentence(l.network_fallback)}.</div> : null}
        </div>
      ) : null}
      <div className="detail-grid">
        <div className="stack-lg">
          <section className="panel">
            <div className="panel-head">
              <h2>Diagnosis</h2>
              {/* A test lead's diagnosis is canned (an agent's test lead says 90%): no confidence to show. */}
              {l.test ? (
                <span className="tag test">Test lead — no real diagnosis</span>
              ) : typeof dx.confidence === 'number' ? (
                <span className="small muted">{Math.round(dx.confidence * 100)}% confident</span>
              ) : null}
            </div>
            <div className="panel-body stack">
              {dx.safety_escalation ? (
                <div className="notice bad">
                  <strong>Safety: {dx.safety_escalation.tier ?? 'escalated'}</strong> {dx.safety_escalation.reason ?? ''}
                </div>
              ) : null}
              {dx.status === 'unavailable_busy' ? (
                // #1018: sent from the widget's "Leave your details" while the
                // analysis was rate limited. There is no diagnosis to show.
                <div className="notice warn" data-testid="busy-lead">
                  <strong>Not diagnosed.</strong> We were too busy to diagnose this request when the homeowner sent it.{' '}
                  {dx.photo_attached ? 'Their photo is below.' : 'No photo came with it; ask the homeowner what is wrong.'}
                </div>
              ) : dx.summary ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>{dx.summary}</p>
              ) : (
                <p className="muted">No summary.</p>
              )}
              <dl className="facts">
                <dt>Trade</dt>
                <dd>{l.trade_label ?? tradeLabel(dx.trade)}</dd>
                <dt>Problem code</dt>
                <dd className={dx.problem_code ? "mono small" : undefined}>{dx.problem_code ?? <NotSet />}</dd>
                <dt>Estimate</dt>
                <dd>{cost && (cost.low || cost.high) ? `$${cost.low ?? '?'}–$${cost.high ?? '?'}` : <NotSet />}</dd>
                <dt>In scope</dt>
                <dd>{l.in_scope ? 'Yes' : 'No'}</dd>
                <dt>Requested</dt>
                <dd>{humanize(l.requested_action)}</dd>
              </dl>
              {l.media.length ? (
                <div className="row">
                  {l.media.map((m) =>
                    m.url && m.content_type.startsWith('image/') ? (
                      <a key={m.media_id} href={m.url} target="_blank" rel="noreferrer">
                        <img src={m.url} alt={`Photo from the homeowner${m.labels.length ? `: ${m.labels.join(', ')}` : ''}`} style={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)' }} />
                      </a>
                    ) : (
                      <a key={m.media_id} className="btn btn-sm" href={m.url ?? undefined}>
                        {m.kind}
                      </a>
                    ),
                  )}
                </div>
              ) : null}
            </div>
          </section>
          <section className="panel">
            <div className="panel-head">
              <h2>Timeline</h2>
            </div>
            <div className="panel-body">
              <ErrorNotice error={resend.error} />
              {timeline.data?.items.length ? (
                <ol className="timeline">
                  {timeline.data.items.map((t, i) => (
                    <TimelineItem
                      key={i}
                      t={t}
                      destination={t.type === 'delivered' ? destinationOf(t.detail.destination_id) : null}
                      vendorName={vendorName}
                      resending={resend.pending}
                      onResend={async (deliveryId) => {
                        const r = await resend.run(deliveryId);
                        if (r) {
                          toast('Delivery queued again');
                          void timeline.reload();
                        }
                      }}
                    />
                  ))}
                </ol>
              ) : (
                <Loading />
              )}
            </div>
          </section>
          <QuoteSlot lead={l} onChanged={refetch} />
        </div>
        <div className="stack-lg">
          <AcceptLeadSlot lead={l} onAccepted={refetch} />
          <section className="panel">
            <div className="panel-head">
              <h2>Homeowner</h2>
            </div>
            <div className="panel-body">
              {l.contact_purged_at ? (
                <p className="muted">Contact details were purged <Time iso={l.contact_purged_at} /> under your retention setting.</p>
              ) : (
                <dl className="facts">
                  <dt>Name</dt>
                  <dd>{[c.first_name, c.last_name].filter(Boolean).map(String).join(' ') || <NotSet />}</dd>
                  <dt>Phone</dt>
                  <dd>{phone ? <a href={`tel:${phone}`}>{formatPhone(phone)}</a> : <NotSet />}</dd>
                  <dt>Email</dt>
                  <dd>{c.email ? <a href={`mailto:${String(c.email)}`}>{String(c.email)}</a> : <NotSet />}</dd>
                  <dt>Address</dt>
                  <dd>{[c.address_1, c.city, c.state, c.postal_code].filter(Boolean).map(String).join(', ') || <NotSet />}</dd>
                  <dt>Window</dt>
                  <dd>{Array.isArray(c.preferred_window) && c.preferred_window.length ? c.preferred_window.join(', ') : <NotSet />}</dd>
                </dl>
              )}
            </div>
          </section>
          <section className="panel">
            <div className="panel-head">
              <h2>Consent record</h2>
            </div>
            <div className="panel-body small stack-sm">
              {/* A test lead has made-up contact details (an agent's has no person at
                  all), so nobody agreed to anything: say so before the wording. */}
              {l.test ? (
                <p className="notice info" style={{ margin: 0 }}>
                  <span>
                    <strong>Test lead — no real consent recorded.</strong>{' '}
                    {(l.consent as Record<string, unknown> | null)?.test === true
                      ? 'An AI agent filed it to check delivery. Nobody was shown any consent wording or agreed to anything.'
                      : 'No homeowner agreed to this. Below is the wording the widget would show.'}
                  </span>
                </p>
              ) : null}
              {l.consent && (l.consent as Record<string, unknown>).test === true ? null : l.consent ? (
                (() => {
                  const k = l.consent as Record<string, unknown>;
                  const text = k.consent_text ?? k.text;
                  return (
                    <>
                      <p style={{ whiteSpace: 'pre-wrap' }} className={l.test ? 'muted' : undefined}>
                        {typeof text === 'string' ? text : <NotSet />}
                      </p>
                      {k.consent_matches_config === false ? (
                        <span className="tag warn" style={{ alignSelf: 'flex-start' }}>
                          Differs from the published consent text
                        </span>
                      ) : null}
                      <dl className="facts">
                        <dt>Mode</dt>
                        <dd>{k.consent_mode ?? k.mode ? humanize(String(k.consent_mode ?? k.mode)) : <NotSet />}</dd>
                        <dt>{l.test ? 'Recorded' : 'Agreed'}</dt>
                        <dd>{formatDateTime(typeof k.consent_timestamp === 'string' ? k.consent_timestamp : l.created_at)}</dd>
                        <dt>Parties</dt>
                        <dd>{Array.isArray(k.consented_parties) && k.consented_parties.length ? k.consented_parties.join(', ') : <NotSet />}</dd>
                        <dt>Page</dt>
                        <dd className="mono">{String(k.page_url ?? l.partner.page_url ?? l.partner.origin)}</dd>
                        <dt>Config</dt>
                        <dd>v{l.partner.widget_config_version ?? 0}</dd>
                      </dl>
                    </>
                  );
                })()
              ) : (
                <span className="muted">No consent record.</span>
              )}
            </div>
          </section>
        </div>
      </div>

      <Modal
        open={closing}
        title="Close this lead"
        onClose={() => setClosing(false)}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setClosing(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={close.pending || !outcome}
              onClick={async () => {
                if (await close.run()) {
                  setClosing(false);
                  toast('Lead closed');
                  void lead.reload();
                  void timeline.reload();
                }
              }}
            >
              {close.pending ? 'Closing…' : 'Close lead'}
            </button>
          </>
        }
      >
        <Field label="Outcome" hint="Required. What actually happened with this lead.">
          {(p) => (
            <select {...p} className="input" value={outcome} required aria-required="true" onChange={(e) => setOutcome(e.target.value)}>
              <option value="" disabled>
                Choose an outcome
              </option>
              {CLOSE_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {humanize(o)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Classification" hint="Feeds the deflection numbers on the dashboard." optional>
          {(p) => (
            <select {...p} className="input" value={outcomeClass} onChange={(e) => setOutcomeClass(e.target.value)}>
              <option value="">Not classified</option>
              {LEAD_OUTCOME_CLASSES.map((o) => (
                <option key={o} value={o}>
                  {humanize(o)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Note" optional>
          {(p) => <textarea {...p} className="input" value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        <ErrorNotice error={close.error} />
      </Modal>

      <Modal
        open={reassigning}
        title="Reassign to a contractor"
        onClose={() => setReassigning(false)}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setReassigning(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={reassign.pending || !/^vnd_[0-9A-HJKMNP-TV-Z]{26}$/.test(vendor.trim())}
              onClick={async () => {
                if (await reassign.run()) {
                  setReassigning(false);
                  toast('Reassigned. The previous contractor’s link no longer works.');
                  void lead.reload();
                  void timeline.reload();
                }
              }}
            >
              {reassign.pending ? 'Reassigning…' : 'Reassign'}
            </button>
          </>
        }
      >
        {roster.loading ? (
          <Loading label="Loading contractors" />
        ) : roster.error ? (
          <ErrorNotice error={roster.error} title="Could not load your contractors" />
        ) : reachable.length ? (
          <Field label="Contractor" hint="Active contractors on your roster. The new one gets a fresh lead brief link.">
            {(p) => (
              <select {...p} className="input" value={vendor} onChange={(e) => setVendor(e.target.value)}>
                <option value="">Choose a contractor…</option>
                {reachable.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                    {v.external_id ? ` (${v.external_id})` : ''} — {v.trades.map((t) => TRADES.find((x) => x.slug === t)?.label ?? t).join(', ')}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <p className="small muted">No active contractors on your roster yet. An owner or admin can add them under Contractors.</p>
        )}
        <Field label="Reason" optional>
          {(p) => <input {...p} className="input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        <ErrorNotice error={reassign.error} />
      </Modal>
    </div>
  );
}
